import { logClientDiagnostic, stashClientDiagnostic } from '@/lib/client-diagnostics';

/**
 * Drop-in replacement for the subset of @sentry/react APIs this codebase
 * was using. Sentry was removed from the stack on 2026-04-17; rather than
 * edit every `Sentry.captureException` / `Sentry.addBreadcrumb` call site,
 * this shim preserves the same method signatures and routes everything to
 * local/dev-preview console diagnostics plus sessionStorage. Production
 * builds keep only redacted browser-side markers.
 *
 * If we ever adopt a new error-reporting vendor (LogRocket, Highlight,
 * Datadog RUM, etc.), swap the implementations here and every existing
 * call site continues to work.
 */

type CaptureContext = {
  tags?: Record<string, string | number | boolean>;
  contexts?: Record<string, unknown>;
  extra?: Record<string, unknown>;
  level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';
};

type Breadcrumb = {
  category?: string;
  message?: string;
  level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';
  data?: Record<string, unknown>;
  type?: string;
};

export function captureException(error: unknown, context?: CaptureContext): void {
  logClientDiagnostic('error', 'captureException', error, context ?? '');
  stashClientDiagnostic(
    'captureException',
    {
      message: (error as Error)?.message ?? String(error),
      stack: (error as Error)?.stack,
      context,
    },
    {
      kind: 'captureException',
      level: context?.level ?? 'error',
      message: 'An exception was captured.',
    },
  );
}

export function captureMessage(message: string, context?: CaptureContext): void {
  logClientDiagnostic('warn', 'captureMessage', message, context ?? '');
}

export function addBreadcrumb(breadcrumb: Breadcrumb): void {
  // Breadcrumbs were Sentry's debug-trail concept. Route to console only —
  // too noisy for sessionStorage, not useful without a backend.
  logClientDiagnostic('debug', 'breadcrumb', breadcrumb);
}

export function setUser(user: { id?: string; email?: string } | null): void {
  logClientDiagnostic('debug', 'setUser', user);
}

/**
 * Sentry.startSpan(cfg, callback) runs callback inside a tracing span.
 * Without a tracing backend we just invoke the callback directly, but we
 * still hand it a stub `span` that implements the subset of the Sentry
 * Span API the codebase touches (setAttribute, setAttributes, setStatus,
 * setData, end). Previously this was `undefined`, which blew up every
 * call site that did `span.setAttribute(...)` — e.g. the chat send path
 * threw "can't access property 'setAttribute', span is undefined" before
 * ever firing the fetch.
 */
type SpanStub = {
  setAttribute: (_key: string, _value: unknown) => void;
  setAttributes: (_attrs: Record<string, unknown>) => void;
  setStatus: (_status: unknown) => void;
  setData: (_key: string, _value: unknown) => void;
  end: () => void;
};

const noopSpan: SpanStub = {
  setAttribute: () => {},
  setAttributes: () => {},
  setStatus: () => {},
  setData: () => {},
  end: () => {},
};

export function startSpan<T>(
  _options: { name: string; op?: string; attributes?: Record<string, unknown> },
  callback: (span: SpanStub) => T,
): T {
  return callback(noopSpan);
}

// Namespace export so existing `import * as Sentry from '@sentry/react'` call
// sites can be swapped to `import * as Sentry from '@/lib/sentry-shim'` with
// zero other edits.
const Sentry = {
  captureException,
  captureMessage,
  addBreadcrumb,
  setUser,
  startSpan,
};
export default Sentry;
